// postfach.js — Postfach als Liste-Detail-Ansicht, gemeinsam für Platzverwaltung (Betreiber ↔ Team) und Verwaltung
// (Team ↔ Betreiber). Server: routes/messages.js. Texte und Adressen kommen vom Bereich (opts), der Ablauf ist gleich.
//   mountPostfach(outlet, { api, base, team, T, id, onCount, newDialog, counterpart })
import { html, attrs, render as put } from '../ui/core/h.js';
import { on } from '../ui/core/dom.js';
import { api as http } from './api.js';
import { fmtDate } from './i18n.js';
import { snackbar } from '../ui/components/snackbar.js';
import './postfach.css';

export const BODY_MAX = 4000;
/** Ungelesen aus Sicht der eigenen Seite. */
export const unreadOf = (c, team) => (team ? c.unread_team : c.unread_operator) || 0;
/** Gesamtzahl ungelesener Nachrichten (Abzeichen der Navigation). */
export const unreadTotal = (list, team) => (list || []).reduce((n, c) => n + unreadOf(c, team), 0);
/** Kurzer Vorschautext der letzten Nachricht (eine Zeile). */
export const snippet = (s, max = 90) => { const v = String(s || '').replace(/\s+/g, ' ').trim(); return v.length > max ? v.slice(0, max - 1) + '…' : v; };

export function mountPostfach(outlet, o) {
  const st = { list: null, error: false, id: o.id || null, thread: null, sending: false };
  const T = o.T;
  const when = (d) => fmtDate(d, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  const loading = () => html`<div class="vc-empty"><span class="vc-loader" role="progressbar" aria-label="${T('loading')}"></span></div>`;

  const itemTpl = (c) => {
    const n = unreadOf(c, o.team);
    return html`<a class="vc-li is-two pb-item${n ? ' is-unread' : ''}" href="${o.base}/nachrichten/${c.id}" data-pb-id="${c.id}"${attrs({ 'aria-current': st.id === c.id ? 'page' : null })}>
      <span class="vc-li-lead"><span class="vc-icon" aria-hidden="true">${c.last_from_team === o.team ? 'send' : 'mail'}</span></span>
      <span class="vc-li-text"><span>${c.subject}${n ? html`<span class="vc-visually-hidden">, ${T('unread', { n })}</span>` : ''}</span>
        <small>${[o.counterpart(c), snippet(c.last_body)].filter(Boolean).join(' · ')}</small></span>
      <span class="vc-li-trail pb-trail"><small class="t-muted">${when(c.last_message_at)}</small>${n ? html`<span class="vc-badge" aria-hidden="true">${n}</span>` : ''}</span></a>`;
  };
  const listTpl = () => {
    if (st.error) return html`<div class="vc-empty"><span class="vc-icon" aria-hidden="true">cloud_off</span><p>${T('load_error')}</p><button type="button" class="vc-btn is-tonal" data-pb-retry>${T('retry')}</button></div>`;
    if (!st.list) return loading();
    return html`${o.newDialog ? html`<div class="pb-actions"><button type="button" class="vc-btn is-tonal" data-pb-new><span class="vc-icon" aria-hidden="true">edit</span>${T('new')}</button></div>` : ''}
      ${st.list.length ? html`<div class="vc-list pb-list">${st.list.map(itemTpl)}</div>`
        : html`<div class="vc-empty"><span class="vc-icon" aria-hidden="true">forum</span><h3>${T('empty')}</h3><p>${T('empty_text')}</p></div>`}`;
  };
  const threadTpl = () => {
    if (!st.id) return html`<div class="vc-empty pb-none"><span class="vc-icon" aria-hidden="true">forum</span><p>${T('pick')}</p></div>`;
    const c = st.thread;
    if (!c) return loading();
    return html`<div class="pb-thread">
      <header class="pb-head">
        <a class="vc-icon-btn pb-back" href="${o.base}/nachrichten" aria-label="${T('back')}"><span class="vc-icon" aria-hidden="true">arrow_back</span></a>
        <div class="pb-title"><h2 class="t-headline-s">${c.subject}</h2>${o.counterpart(c) ? html`<p class="t-body-m t-muted">${o.counterpart(c)}</p>` : ''}</div></header>
        <ol class="pb-msgs">${c.messages.map((m) => html`<li class="pb-msg${m.from_team === o.team ? ' is-own' : ''}">
          <p class="pb-bubble">${m.body}</p><small class="t-muted">${m.from_team === o.team ? T('you') : (m.from_team ? T('team') : (c.operator_name || T('operator')))} · ${when(m.created_at)}</small></li>`)}</ol>
        <form class="pb-compose" data-pb-form>
          <label class="vc-field is-outlined"><textarea name="body" rows="3" maxlength="${BODY_MAX}" required data-pb-body></textarea><span class="vc-field-label">${T('reply')}</span></label>
          <button type="submit" class="vc-btn" data-pb-send${attrs({ disabled: st.sending })}><span class="vc-icon" aria-hidden="true">send</span>${T('send')}</button>
        </form></div>`;
  };
  put(outlet, html`<div class="vc-panes pb-panes" data-detail="${!!st.id}">
    <section class="vc-pane is-list pb-pane-list" aria-label="${T('title')}"><header class="vc-topbar-large"><h1 class="t-headline-m">${T('title')}</h1></header><div data-pb-list></div></section>
    <section class="vc-pane is-detail pb-pane-detail" data-pb-thread aria-live="polite"></section></div>`);
  const drawList = () => put(outlet.querySelector('[data-pb-list]'), listTpl());
  const drawThread = () => { outlet.querySelector('.pb-panes').dataset.detail = String(!!st.id); put(outlet.querySelector('[data-pb-thread]'), threadTpl()); const ol = outlet.querySelector('.pb-msgs'); if (ol) ol.lastElementChild?.scrollIntoView({ block: 'nearest' }); };

  async function loadList() {
    const r = await http.get(o.api);
    st.error = !r.ok; st.list = r.ok ? r.data.conversations || [] : null;
    drawList();
    if (o.onCount && st.list) o.onCount(unreadTotal(st.list, o.team));
  }
  async function loadThread() {
    if (!st.id) { drawThread(); return; }
    st.thread = null; drawThread();
    const r = await http.get(o.api + '/' + st.id);
    if (!r.ok) { st.thread = null; put(outlet.querySelector('[data-pb-thread]'), html`<div class="vc-empty"><span class="vc-icon" aria-hidden="true">search_off</span><p>${T('not_found')}</p></div>`); return; }
    st.thread = r.data; drawThread();
    if (unreadOf(r.data, o.team)) { await http.post(o.api + '/' + st.id + '/read', {}); loadList(); }
  }
  const offs = [
    on(outlet, 'click', '[data-pb-retry]', loadList),
    on(outlet, 'click', '[data-pb-new]', async () => { const id = await o.newDialog(); if (id) { await loadList(); o.go('/nachrichten/' + id); } }),
    on(outlet, 'submit', '[data-pb-form]', async (e) => {
      e.preventDefault();
      const ta = outlet.querySelector('[data-pb-body]');
      const body = ta.value.trim();
      if (!body || st.sending) return;
      st.sending = true;
      const r = await http.post(o.api + '/' + st.id, { body });
      st.sending = false;
      if (!r.ok) { snackbar({ text: T('send_error'), error: true }); return; }
      ta.value = '';
      await loadThread(); loadList();
    }),
  ];
  loadList(); loadThread();
  return {
    update(id) { st.id = id || null; for (const a of outlet.querySelectorAll('[data-pb-id]')) { if (Number(a.dataset.pbId) === st.id) a.setAttribute('aria-current', 'page'); else a.removeAttribute('aria-current'); } loadThread(); },
    dispose() { offs.forEach((f) => f()); },
  };
}
